import React, { useState } from "react";
import { motion } from "framer-motion";
import StarRating from "./StarRating";

const LawFirmDashboard = ({ userData, setUserData }) => {
    const [activeSubTab, setActiveSubTab] = useState("overview");
    const [showAddLawyerModal, setShowAddLawyerModal] = useState(false);
    const [newLawyerData, setNewLawyerData] = useState({
        name: "",
        email: "",
        specialization: "",
        experience: "",
        languages: []
    });

    const firmLawyers = [
        {
            id: 1,
            name: "Jennifer Martinez",
            email: "j.martinez@firm.com",
            specialization: "Immigration Law",
            experience: "5 years",
            languages: ["English", "Spanish"],
            status: "active",
            rating: 4.8,
            totalReviews: 45,
            clients: 23,
            revenue: 4500
        },
        {
            id: 2,
            name: "David Kim",
            email: "d.kim@firm.com",
            specialization: "Employment Immigration",
            experience: "3 years",
            languages: ["English", "Korean"],
            status: "active",
            rating: 4.6,
            totalReviews: 32,
            clients: 18,
            revenue: 3200
        },
        {
            id: 3,
            name: "Lisa Thompson",
            email: "l.thompson@firm.com",
            specialization: "Family Immigration",
            experience: "7 years",
            languages: ["English", "French"],
            status: "inactive",
            rating: 4.9,
            totalReviews: 58,
            clients: 31,
            revenue: 6200
        },
        {
            id: 4,
            name: "Michael Rodriguez",
            email: "m.rodriguez@firm.com",
            specialization: "Criminal Immigration",
            experience: "4 years",
            languages: ["English", "Spanish"],
            status: "active",
            rating: 4.7,
            totalReviews: 28,
            clients: 15,
            revenue: 3800
        },
        {
            id: 5,
            name: "Sarah Chen",
            email: "s.chen@firm.com",
            specialization: "Business Immigration",
            experience: "6 years",
            languages: ["English", "Mandarin"],
            status: "active",
            rating: 4.5,
            totalReviews: 41,
            clients: 22,
            revenue: 5200
        }
    ];

    const clientRequests = [
        {
            id: 1,
            clientName: "John Smith",
            email: "john.smith@email.com",
            phone: "+1 (555) 123-4567",
            caseType: "I-130 Petition",
            urgency: "high",
            date: "2024-01-25",
            status: "new",
            message: "Need urgent help with family petition",
            assignedLawyer: null,
            estimatedFee: 2500
        },
        {
            id: 2,
            clientName: "Maria Garcia",
            email: "maria.garcia@email.com",
            phone: "+1 (555) 234-5678",
            caseType: "Employment Authorization",
            urgency: "medium",
            date: "2024-01-24",
            status: "in-progress",
            message: "Looking for assistance with work permit application",
            assignedLawyer: "Jennifer Martinez",
            estimatedFee: 1800
        },
        {
            id: 3,
            clientName: "Ahmed Hassan",
            email: "ahmed.hassan@email.com",
            phone: "+1 (555) 345-6789",
            caseType: "Naturalization",
            urgency: "low",
            date: "2024-01-23",
            status: "completed",
            message: "Citizenship application support needed",
            assignedLawyer: "David Kim",
            estimatedFee: 3200
        },
        {
            id: 4,
            clientName: "Sarah Johnson",
            email: "sarah.johnson@email.com",
            phone: "+1 (555) 456-7890",
            caseType: "Family Reunification",
            urgency: "high",
            date: "2024-01-26",
            status: "new",
            message: "Emergency family reunification case",
            assignedLawyer: null,
            estimatedFee: 4500
        },
        {
            id: 5,
            clientName: "Carlos Rodriguez",
            email: "carlos.rodriguez@email.com",
            phone: "+1 (555) 567-8901",
            caseType: "Asylum Application",
            urgency: "medium",
            date: "2024-01-22",
            status: "in-progress",
            message: "Asylum application review needed",
            assignedLawyer: "Lisa Thompson",
            estimatedFee: 2800
        }
    ];

    const subscriptionLimits = {
        Gold: { lawyers: 15, forms: 50, clients: 100 },
        Silver: { lawyers: 8, forms: 25, clients: 50 },
        Bronze: { lawyers: 3, forms: 10, clients: 20 }
    };

    const currentLimits = subscriptionLimits[userData.subscriptionType] || subscriptionLimits.Gold;

    const handleAddLawyer = (e) => {
        e.preventDefault();
        // Mock adding lawyer
        setShowAddLawyerModal(false);
        setNewLawyerData({
            name: "",
            email: "",
            specialization: "",
            experience: "",
            languages: []
        });
    };

    const handleAssignRequest = (requestId, lawyerId) => {
    };

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Subscription Info */}
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg p-6 border border-yellow-200">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">Current Subscription</h3>
                        <p className="text-gray-600">You're on the {userData.subscriptionType} plan</p>
                        <p className="text-sm text-gray-500 mt-1">Renewal: March 15, 2024</p>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-bold text-yellow-600">{userData.subscriptionType}</div>
                        <div className="text-sm text-gray-600">Plan</div>
                        <div className="text-xs text-gray-500">$299/month</div>
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-4 gap-4">
                    <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">{firmLawyers.length}/{currentLimits.lawyers}</div>
                        <div className="text-sm text-gray-600">Lawyers</div>
                        <div className="text-xs text-gray-500">{currentLimits.lawyers - firmLawyers.length} slots left</div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">45/{currentLimits.forms}</div>
                        <div className="text-sm text-gray-600">Forms</div>
                        <div className="text-xs text-gray-500">{currentLimits.forms - 45} remaining</div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">72/{currentLimits.clients}</div>
                        <div className="text-sm text-gray-600">Clients</div>
                        <div className="text-xs text-gray-500">{currentLimits.clients - 72} capacity</div>
                    </div>
                    <div className="text-center">
                        <div className="text-lg font-semibold text-gray-900">24/7</div>
                        <div className="text-sm text-gray-600">Support</div>
                        <div className="text-xs text-gray-500">Priority</div>
                    </div>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">Active Lawyers</p>
                            <p className="text-2xl font-bold text-blue-600">
                                {firmLawyers.filter(l => l.status === 'active').length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">Total Clients</p>
                            <p className="text-2xl font-bold text-green-600">
                                {firmLawyers.reduce((sum, l) => sum + l.clients, 0)}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">Pending Requests</p>
                            <p className="text-2xl font-bold text-yellow-600">
                                {clientRequests.filter(r => r.status === 'pending').length}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">Monthly Revenue</p>
                            <p className="text-2xl font-bold text-purple-600">
                                ${firmLawyers.reduce((sum, l) => sum + l.revenue, 0).toLocaleString()}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderLawyers = () => (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Firm Lawyers</h3>
                <motion.button
                    onClick={() => setShowAddLawyerModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Add Lawyer
                </motion.button>
            </div>

            <div className="space-y-4">
                {firmLawyers.map((lawyer, index) => (
                    <motion.div
                        key={lawyer.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                    <span className="text-blue-600 font-semibold">
                                        {lawyer.name.split(' ').map(n => n[0]).join('')}
                                    </span>
                                </div>
                                <div>
                                    <div className="flex items-center space-x-2 mb-1">
                                        <h4 className="font-semibold text-gray-900">{lawyer.name}</h4>
                                        <StarRating rating={lawyer.rating} size="xs" showNumber={true} />
                                    </div>
                                    <p className="text-sm text-gray-600">{lawyer.specialization}</p>
                                    <p className="text-sm text-gray-500">{lawyer.experience} • {lawyer.clients} clients • {lawyer.totalReviews} reviews</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-4">
                                <div className="text-right">
                                    <div className="text-sm font-medium text-gray-900">${lawyer.revenue.toLocaleString()}</div>
                                    <div className="text-xs text-gray-500">Revenue</div>
                                </div>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${lawyer.status === 'active'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-gray-100 text-gray-800'
                                    }`}>
                                    {lawyer.status}
                                </span>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );

    const renderRequests = () => (
        <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900">Client Requests</h3>

            <div className="space-y-4">
                {clientRequests.map((request, index) => (
                    <motion.div
                        key={request.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex-1">
                                <div className="flex items-center space-x-3 mb-2">
                                    <h4 className="font-semibold text-gray-900">{request.clientName}</h4>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${request.urgency === 'high'
                                        ? 'bg-red-100 text-red-800'
                                        : request.urgency === 'medium'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-green-100 text-green-800'
                                        }`}>
                                        {request.urgency} priority
                                    </span>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${request.status === 'new'
                                        ? 'bg-blue-100 text-blue-800'
                                        : request.status === 'in-progress'
                                            ? 'bg-yellow-100 text-yellow-800'
                                            : 'bg-green-100 text-green-800'
                                        }`}>
                                        {request.status}
                                    </span>
                                </div>
                                <p className="text-gray-600 mb-2">{request.caseType}</p>
                                <p className="text-sm text-gray-500 mb-2">{request.message}</p>
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                    <span>{request.email}</span>
                                    <span>{request.phone}</span>
                                    <span>{new Date(request.date).toLocaleDateString()}</span>
                                </div>
                            </div>
                            <div className="flex flex-col space-y-2 ml-4">
                                <motion.button
                                    onClick={() => handleAssignRequest(request.id, 1)}
                                    className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors duration-200"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    Assign
                                </motion.button>
                                <motion.button
                                    className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    View Details
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
    );

    return (
        <div>
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Law Firm Dashboard</h2>
                    <p className="text-gray-600 mt-1">Manage your law firm operations and team</p>
                </div>
            </div>

            {/* Sub Navigation */}
            <div className="mb-6">
                <nav className="flex space-x-8">
                    {[
                        { id: "overview", label: "Overview" },
                        { id: "lawyers", label: "Lawyers" },
                        { id: "requests", label: "Client Requests" }
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveSubTab(tab.id)}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeSubTab === tab.id
                                ? "border-blue-500 text-blue-600"
                                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Content */}
            {activeSubTab === "overview" && renderOverview()}
            {activeSubTab === "lawyers" && renderLawyers()}
            {activeSubTab === "requests" && renderRequests()}

            {/* Add Lawyer Modal */}
            {showAddLawyerModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
                    >
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Lawyer</h3>
                        <form onSubmit={handleAddLawyer} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                                <input
                                    type="text"
                                    value={newLawyerData.name}
                                    onChange={(e) => setNewLawyerData(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    value={newLawyerData.email}
                                    onChange={(e) => setNewLawyerData(prev => ({ ...prev, email: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Specialization</label>
                                <input
                                    type="text"
                                    value={newLawyerData.specialization}
                                    onChange={(e) => setNewLawyerData(prev => ({ ...prev, specialization: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Experience</label>
                                <input
                                    type="text"
                                    value={newLawyerData.experience}
                                    onChange={(e) => setNewLawyerData(prev => ({ ...prev, experience: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="e.g., 5 years"
                                    required
                                />
                            </div>
                            <div className="flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowAddLawyerModal(false)}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                                >
                                    Add Lawyer
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default LawFirmDashboard;
